import React, { useState, useEffect } from 'react';
import { Send, Users, MessageSquare, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { contactService } from '../../services/contactService';
import { templateService } from '../../services/templateService';
import sseService from '../../services/sseService';
import toast from 'react-hot-toast';

const TestMessagePage = () => {
  const [contactGroups, setContactGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParameters, setTemplateParameters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadContactGroups();
    loadTemplates();
  }, []);

  // Connect to SSE for real-time updates
  useEffect(() => {
    const customerId = localStorage.getItem('customerId') || '5003964b-5496-49d9-bbfc-303000649716';
    sseService.connect(customerId);
    
    sseService.addEventListener('message-status-update', handleMessageStatusUpdate);
    
    return () => {
      sseService.removeEventListener('message-status-update', handleMessageStatusUpdate);
      sseService.disconnect();
    };
  }, []);

  const loadContactGroups = async () => {
    try {
      const response = await contactService.getContactGroups();
      console.log('Contact groups response:', response);
      setContactGroups(response || []);
    } catch (error) {
      console.error('Error loading contact groups:', error);
      toast.error('Failed to load contact groups');
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await templateService.getApprovedTemplates();
      console.log('Templates response:', response);
      setTemplates(response || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const loadGroupDetails = async (groupId) => {
    try {
      setIsLoading(true);
      // Use the data that's already available from the contact groups list
      const group = contactGroups.find(g => g.groupId === groupId);
      if (group) {
        setGroupDetails({
          totalContacts: group.totalContacts,
          subscribedContacts: group.subscribedCount
        });
      }
    } catch (error) {
      console.error('Error loading group details:', error);
      toast.error('Failed to load group details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGroupChange = (groupId) => {
    const group = contactGroups.find(g => g.groupId === groupId);
    setSelectedGroup(group);
    if (groupId) {
      loadGroupDetails(groupId);
    } else {
      setGroupDetails(null);
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.templateId === templateId);
    setSelectedTemplate(template);
    
    if (template) {
      // Extract variables from template content and header
      const bodyVariables = extractVariables(template.content);
      const headerVariables = extractVariables(template.headerText);
      const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
      
      // Initialize template parameters
      const parameters = allVariables.map(varNum => ({
        variableNumber: varNum,
        parameterType: 'TEXT',
        hardcodedValue: '',
        contactField: 'contactName'
      }));
      
      setTemplateParameters(parameters);
    } else {
      setTemplateParameters([]);
    }
  };

  const extractVariables = (content) => {
    if (!content) return [];
    // Match both numeric ({{1}}, {{2}}) and string-based ({{name}}, {{couponcode}}) parameters
    const matches = content.match(/\{\{([^}]+)\}\}/g);
    if (!matches) return [];
    const variables = [...new Set(matches.map(match => match.replace(/\{\{|\}\}/g, '')))];
    // Sort: numeric parameters first (numerically), then string parameters (alphabetically)
    return variables.sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      const isNumA = !isNaN(numA) && a.trim() === String(numA);
      const isNumB = !isNaN(numB) && b.trim() === String(numB);
      
      if (isNumA && isNumB) {
        return numA - numB; // Both numeric, sort numerically
      } else if (isNumA) {
        return -1; // Numeric comes before string
      } else if (isNumB) {
        return 1; // String comes after numeric
      } else {
        return a.localeCompare(b); // Both strings, sort alphabetically
      }
    });
  };

  const handleParameterChange = (index, field, value) => {
    const updated = [...templateParameters];
    updated[index] = { ...updated[index], [field]: value };
    setTemplateParameters(updated);
  };

  const handleSendMessages = async () => {
    if (!selectedGroup || !selectedTemplate) {
      toast.error('Please select both a contact group and a template');
      return;
    }

    if (templateParameters.some(param => 
        param.parameterType === 'TEXT' && !param.hardcodedValue.trim()
    )) {
      toast.error('Please provide values for all text parameters');
      return;
    }

    try {
      setIsSending(true);
      setSendResults(null);

      const request = {
        contactGroupId: selectedGroup.groupId,
        templateId: selectedTemplate.templateId,
        templateName: selectedTemplate.templateName,
        language: selectedTemplate.language,
        templateParameters: templateParameters
      };

      const response = await templateService.sendTestMessages(request);
      setSendResults(response.data);
      
      if (response.data.success) {
        toast.success(`Messages sent! Total: ${response.data.totalContacts}, Sent: ${response.data.messagesSent}, Failed: ${response.data.messagesFailed}`);
      } else {
        toast.error(response.data.message);
      }

    } catch (error) {
      console.error('Error sending messages:', error);
      
      // Handle specific error types
      if (error.response?.status === 403 && error.response?.data?.message?.includes('License')) {
        toast.error('License Not Active - Please contact administrator to activate your license');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.message || 'Failed to send messages');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageStatusUpdate = (event) => {
    console.log('Message status update:', event.data);
    // Handle real-time status updates
    // This could update the UI to show delivery/read status
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Test Bulk Messages</h1>
        <p className="text-gray-600">Send test messages to contact groups using approved templates</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Configuration Panel */}
        <div className="space-y-6">
          {/* Contact Group Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Select Contact Group
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Group
                </label>
                <select
                  value={selectedGroup?.groupId || ''}
                  onChange={(e) => handleGroupChange(e.target.value)}
                  className="w-full input-field"
                  disabled={isSending}
                >
                  <option value="">Select a contact group</option>
                  {contactGroups.map(group => (
                    <option key={group.groupId} value={group.groupId}>
                      {group.groupName}
                    </option>
                  ))}
                </select>
              </div>

              {groupDetails && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Group Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Contacts:</span>
                      <span className="ml-2 font-medium">{groupDetails.totalContacts}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Subscribed:</span>
                      <span className="ml-2 font-medium text-green-600">{groupDetails.subscribedContacts}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Template Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Select Template
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Template
                </label>
                <select
                  value={selectedTemplate?.templateId || ''}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="w-full input-field"
                  disabled={isSending}
                >
                  <option value="">Select a template</option>
                  {templates.map(template => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.templateName} ({template.templateCategory})
                    </option>
                  ))}
                </select>
              </div>

              {selectedTemplate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Template Preview</h3>
                  <div className="text-sm text-gray-700">
                    <div className="mb-2">
                      <span className="font-medium">Content:</span>
                      <p className="mt-1 p-2 bg-white rounded border">{selectedTemplate.content}</p>
                    </div>
                    {selectedTemplate.headerText && (
                      <div>
                        <span className="font-medium">Header:</span>
                        <p className="mt-1 p-2 bg-white rounded border">{selectedTemplate.headerText}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Template Parameters Panel */}
        <div className="space-y-6">
          {templateParameters.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Template Parameters
              </h2>
              
              <div className="space-y-4">
                {templateParameters.map((param, index) => (
                  <div key={param.variableNumber} className="border rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">
                      Variable {`{{${param.variableNumber}}}`}
                    </h3>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parameter Type
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`paramType_${index}`}
                              value="TEXT"
                              checked={param.parameterType === 'TEXT'}
                              onChange={(e) => handleParameterChange(index, 'parameterType', e.target.value)}
                              className="mr-2"
                              disabled={isSending}
                            />
                            Text
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`paramType_${index}`}
                              value="CONTACT_FIELD"
                              checked={param.parameterType === 'CONTACT_FIELD'}
                              onChange={(e) => handleParameterChange(index, 'parameterType', e.target.value)}
                              className="mr-2"
                              disabled={isSending}
                            />
                            Contact Name
                          </label>
                        </div>
                      </div>

                      {param.parameterType === 'TEXT' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Value
                          </label>
                          <input
                            type="text"
                            value={param.hardcodedValue}
                            onChange={(e) => handleParameterChange(index, 'hardcodedValue', e.target.value)}
                            placeholder={`Enter value for {{${param.variableNumber}}}`}
                            className="w-full input-field"
                            disabled={isSending}
                          />
                        </div>
                      )}

                      {param.parameterType === 'CONTACT_FIELD' && (
                        <div className="text-sm text-gray-600">
                          Will use contact name (fallback to phone number if name is null)
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send Button */}
          <div className="bg-white rounded-lg shadow p-6">
            <button
              onClick={handleSendMessages}
              disabled={!selectedGroup || !selectedTemplate || isSending}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Sending Messages...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Send Test Messages
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results Panel */}
      {sendResults && (
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            Send Results
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{sendResults.totalContacts}</div>
              <div className="text-sm text-gray-600">Total Contacts</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{sendResults.messagesSent}</div>
              <div className="text-sm text-gray-600">Messages Sent</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{sendResults.messagesFailed}</div>
              <div className="text-sm text-gray-600">Messages Failed</div>
            </div>
          </div>

          {sendResults.results && sendResults.results.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Detailed Results</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message ID</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sendResults.results.map((result, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {result.contactName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.phoneNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {result.success ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Sent
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.messageId || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default TestMessagePage;
